import { RESERVED_SUBDOMAINS } from "@/constants/subdomains";

// Hostnames that are considered "the root domain" — visiting them directly
// (no subdomain) renders the Landing page. Includes the production apex
// domain, the current Firebase Hosting domain (kept for the existing
// path-based deploy), and local dev hosts so `npm start` keeps working
// without any subdomain wiring.
export const ROOT_HOSTNAMES = [
  "visual360.app",
  "visual360-prod.web.app",
  "visual360-prod.firebaseapp.com",
  // Staging's Firebase Hosting domains (path-based, no subdomains): without
  // them "visual-360-staging" was read as a project slug and every page 404'd.
  "visual-360-staging.web.app",
  "visual-360-staging.firebaseapp.com",
  "visual360.cc",
  "localhost",
  "127.0.0.1",
];

/**
 * Resolves which "app" should render based on window.location.hostname.
 *
 * - Root domain (or no recognizable subdomain, e.g. a bare dev host) → "landing"
 * - First subdomain segment "admin" → "admin"
 * - First subdomain segment "app"   → "client"
 * - Any other reserved subdomain (api, assets, www) → "landing" (not a frontend app)
 * - Anything else → "viewer", with that first segment as the project slug
 *
 * @param {string} [hostnameOverride] mainly for tests — defaults to window.location.hostname
 * @returns {{ mode: "landing" | "admin" | "client" | "viewer", projectSlug: string | null, subdomain: string | null }}
 */
export function resolveAppMode(hostnameOverride) {
  const hostname =
    hostnameOverride ??
    (typeof window !== "undefined" ? window.location.hostname : "");

  const host = (hostname || "").toLowerCase();

  if (!host) {
    return { mode: "landing", projectSlug: null, subdomain: null };
  }

  if (ROOT_HOSTNAMES.includes(host)) {
    return { mode: "landing", projectSlug: null, subdomain: null };
  }

  // No dot at all — some unrecognized bare dev host (e.g. a LAN hostname or
  // an IP). Safer to fall back to landing than to misread it as a project slug.
  if (!host.includes(".")) {
    return { mode: "landing", projectSlug: null, subdomain: null };
  }

  // Cloudflare's own generic preview domain (e.g. *.workers.dev) — the
  // project name IS the first segment here, so it's not a real subdomain
  // of ours. Treat ad-hoc previews on this domain as landing rather than
  // misreading the Worker's own name as a project slug.
  if (host.endsWith(".workers.dev")) {
    return { mode: "landing", projectSlug: null, subdomain: null };
  }

  const firstSegment = host.split(".")[0];

  // www.visual360.app (or www.<any root hostname>) — still landing.
  if (
    firstSegment === "www" &&
    ROOT_HOSTNAMES.some((root) => host === `www.${root}`)
  ) {
    return { mode: "landing", projectSlug: null, subdomain: firstSegment };
  }

  if (firstSegment === "admin") {
    return { mode: "admin", projectSlug: null, subdomain: firstSegment };
  }

  if (firstSegment === "app") {
    return { mode: "client", projectSlug: null, subdomain: firstSegment };
  }

  // Other reserved subdomains (api, assets, or a stray www that didn't match
  // a known root above) aren't a frontend app — treat as landing rather than
  // guessing it's a project slug.
  if (RESERVED_SUBDOMAINS.includes(firstSegment)) {
    return { mode: "landing", projectSlug: null, subdomain: firstSegment };
  }

  return { mode: "viewer", projectSlug: firstSegment, subdomain: firstSegment };
}
